import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Navbar } from '@/components/layout/Navbar';
import { api } from '@/lib/api-client';
import { Product } from '@shared/types';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, ShoppingCart, AlertCircle, CalendarClock, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useFarmStore } from '@/store/useFarmStore';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/use-currency';

export function CatalogPage() {
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [onlySubs, setOnlySubs] = useState(false);
  const addToCart = useFarmStore((s) => s.addToCart);
  const { formatFromBase } = useCurrency();

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['products', filter, search, onlySubs],
    queryFn: ({ pageParam }) => api<{ items: Product[]; hasMore: boolean; cursor: string | null }>(`/api/products?cursor=${pageParam || ''}&pageSize=20&category=${filter}&search=${encodeURIComponent(search)}`),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
  });

  const allProducts = data?.pages.flatMap(page => page.items) ?? [];
  // Server-side filtering by category and search text; client-side filtering for subscription only
  const filtered = allProducts.filter(p => {
    const matchesSub = onlySubs ? p.isSubscriptionAvailable : true;
    return matchesSub;
  });
  const handleAddToCart = (product: Product) => {
    addToCart([{ product, quantity: 1, subtotal: product.price, frequency: 'one-time' }]);
    toast.success(`${product.name} added to cart`);
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Marketplace</h1>
            <p className="text-sm text-muted-foreground">Premium farming inputs and specialized equipment catalog.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9 h-10 rounded-md border-border bg-background focus:ring-1 focus:ring-primary"
                placeholder="Find resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant={onlySubs ? "default" : "outline"}
              size="sm"
              className="h-10 text-xs px-4"
              onClick={() => setOnlySubs(!onlySubs)}
            >
              <CalendarClock className="mr-2 h-4 w-4" /> Subscriptions
            </Button>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide border-b border-border/60">
          {['all', 'poultry', 'fish', 'cattle', 'goats', 'rabbits'].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={cn(
                "capitalize text-[11px] font-bold uppercase tracking-widest px-4 py-3 whitespace-nowrap transition-all border-b-2 relative",
                filter === cat 
                  ? "border-primary text-primary" 
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {cat}
            </button>
          ))}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-64 w-full rounded-md" />
            ))}
          </div>
        ) : isError ? (
          <div className="text-center py-20 border rounded-md bg-muted/10 space-y-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm font-medium">Network connectivity issue.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Reconnect</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {filtered.map((product) => (
              <Card key={product.id} className="rounded-md border shadow-sm bg-background flex flex-col group transition-all hover:border-primary/50 overflow-hidden">
                <CardHeader className="p-4 pb-2 space-y-1 relative">
                  {(product.stockCount || 0) < 20 && (
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-orange-600 text-white text-[8px] font-bold uppercase rounded-sm shadow-sm z-10 flex items-center gap-1">
                      {(product.stockCount || 0) === 0 ? 'Out of Stock' : (product.stockCount || 0) < 10 ? `Low Stock` : `Only ${product.stockCount} left`}
                    </div>
                  )}
                  <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{product.subcategory}</div>
                  <CardTitle className="text-xs font-bold line-clamp-2 min-h-[2.5rem] leading-tight group-hover:text-primary transition-colors">
                    {product.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 flex-1">
                  <p className="text-[10px] text-muted-foreground line-clamp-4 leading-relaxed italic">
                    {product.description || "Premium agricultural input optimized for commercial yield."}
                  </p>
                </CardContent>
                <CardFooter className="p-4 pt-2 border-t flex flex-col gap-2 bg-muted/5">
                  <div className="w-full flex justify-between items-center">
                    <div className="text-xs font-bold text-foreground">
                      {formatFromBase(product.price)}
                      <span className="text-[9px] font-normal text-muted-foreground ml-1">/{product.unit}</span>
                    </div>
                    {product.isSubscriptionAvailable && (
                      <div className="text-[9px] font-bold text-primary flex items-center gap-1">
                        <CalendarClock className="h-2.5 w-2.5" /> Plan Available
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="h-8 w-full text-[10px] font-bold uppercase tracking-wider rounded-sm group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all" onClick={() => handleAddToCart(product)}>
                    Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
        {!isLoading && !isError && hasNextPage && (
          <div className="flex justify-center pt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="h-10 px-6 text-xs font-bold uppercase tracking-wider"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
                </>
              ) : (
                'Load More Products'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}